import { CaretDownOutlined } from '@ant-design/icons';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { Editor } from '@tinymce/tinymce-react';
import {
  AssignmentIndOutlined,
  FileSignatureOutlined,
  InfoOutlined,
  PrintOutlined,
} from '@vindr/icons';
import { useGetAccounts } from '@vindr/services/actions/account.action';
import { useGetKeyImages } from '@vindr/services/actions/attachment.action';
import {
  useGetEsignHistoryDetail,
  useGetFullEsignHistoryByOrder,
} from '@vindr/services/actions/esign.action';
import { useGetLayouts } from '@vindr/services/actions/layout.action';
import { useGetOrderDetail, useGetOrders } from '@vindr/services/actions/order.action';
import { useGetPrintPDF } from '@vindr/services/actions/print.action';
import {
  useGetLatestReportByOrder,
  useGetReportByVersionId,
  useGetReportVersions,
  useUpdateApproveReport,
  useUpdateCancelReport,
  useUpdateEditReport,
  useUpdateSubmitReport,
} from '@vindr/services/actions/report.action';
import { useGetDetailStudy } from '@vindr/services/actions/study.action';
import { ESIGN_STATUS } from '@vindr/services/apis/esign.api';
import {
  INDICATOR,
  LatestReportByOrder,
  ParamsUpdateSubmitReport,
  ResponseCreateReport,
  UpdateEditReportErrorResponse,
} from '@vindr/services/apis/report.api';
import { Section } from '@vindr/services/apis/templateStructuredReport';
import { GENDER } from '@vindr/services/constants/common.constant';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { queryKey } from '@vindr/services/constants/queryClientKey';
import { LAYOUT_TYPE, REPORT_STATUS } from '@vindr/services/constants/report.constant';
import {
  ALLTIME_STUDYDATE,
  MATCH_STATUS,
  MODALITIES,
} from '@vindr/services/constants/study.constant';
import keycloak from '@vindr/services/keycloak';
import { Empty, FormItem } from '@vindr/ui';
import { generateUserLabel } from '@vindr/ui/src/components/common/FormItem/components/Technician';
import { StructuredReportHandle } from '@vindr/ui/src/components/common/StructuredReport/StructuredReportForm';
import Loading from '@vindr/ui/src/components/ui/Loading';
import { decodeHTMLEntities } from '@vindr/utils/converter';
import { formatDateColumn } from '@vindr/utils/dateTime';
import { validateEmptyString } from '@vindr/utils/helpers';
import {
  Omit,
  addToArray,
  conditionObj,
  enumKeys,
  getArrayVal,
  getDateFormatted,
} from '@vindr/utils/helpers/common.helper';
import useToggle from '@vindr/utils/hooks/useToggle';
import { isMobile } from '@vindr/utils/mobile';
import waitForReady from '@vindr/utils/state/waitForReady';
import {
  Empty as AntEmpty,
  Button,
  Col,
  ConfigProvider,
  Dropdown,
  Form,
  FormInstance,
  Row,
  Select,
  Tooltip,
} from 'antd';
import { MenuItemType } from 'antd/es/menu/hooks/useItems';
import dayjs from 'dayjs';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Updater, useImmer } from 'use-immer';
import Assign from '../Assign';
import { CaptureHandle } from '../Capture-v2/Webcam/context/type';
import WebcamRecorder from '../Capture-v2/WebcamRecorder';
import MatchOrder from '../MatchOrder';
import CreateOrderModal from '../Order/CreateOrder/CreateOrderModal';
import EditOrder from '../Order/EditOrder';
import OpenWebcamButton from './OpenWebcamButton';
import PrintModal from './Print';
import './ReportForm.css';
import SelectTemplate, { isStructureReport } from './SelectTemplate';
import SignDigital from './SignDigital';
import CompareEditor from './components/CompareEditor';
import History from './components/History';
import KeyImageDisplay from './components/KeyImageDisplay';
import { ReportEditor } from './components/ReportEditor';
import ReportHeader from './components/ReportHeader';
import useContentConclusion from './components/useContentConclusion';
import Report from './index';
import useGenerateReport, { MergedReport } from './useGenerateReport';
import TestResult from './TestResult';

const ReportForm = ({
  study_id = null,
  order_id = null,
  assign = null,
  attachmentParams,
  onMissingOrder = null,
  recordVideo,
  setRecordVideo,
  warningCloseCamera,
  message,
  iframe,
  modal,
}: Omit<ReportFormProps, 'onCancel' | 'visible' | 'motion'>) => {
  const { t } = useTranslation('Vindoc');
  const { EMPTY_LINE, LOCK_EMPTY_LINE, CONTENT_TITLE, CONCLUSION_TITLE } = useContentConclusion();
  const [iframeState] = iframe || [];
  const userInfo = keycloak.getUserInfo();
  const [openPrintDialog, setOpenPrintDialog] = useState<boolean>(false);
  const [openSelectTemplate, setOpenSelectTemplate] = useState<boolean>(false);
  const [openMatchOrder, setOpenMatchOrder] = useState<boolean>(false);
  const [openCreateOrder, setOpenCreateOrder] = useState<boolean>(false);
  const [editOrder, setEditOrder] = useState({ editingOrder: null, open: false });
  const [assignState, setAssignState] = useState({ visible: false, order: null });
  const [pendingOrderIds, setPendingOrderIds] = useState<string[]>([]);
  const openRecordVideo = useMemo(() => recordVideo.open, [recordVideo.open]);
  const [isUploading, setIsUploading] = useState(false);
  const { value: isCompare, setTrue: openCompare, setFalse: closeCompare } = useToggle(false);
  const {
    value: isOpenTestResult,
    setTrue: openTestResult,
    setFalse: closeTestResult,
  } = useToggle(false);
  const queryClient = useQueryClient();
  const printRef = useRef<Editor>(null);
  const previewRef = useRef<Editor>(null);
  const [selectedCompareOrder, setSelectedCompareOrder] =
    useState<ReportFormStates['selectedOrder']>(null);

  const { data: responseStudy } = useGetDetailStudy(
    { study_id, ...ALLTIME_STUDYDATE },
    { enabled: !!study_id }
  );
  const study = useMemo(() => responseStudy?.data, [responseStudy?.data]);

  const { data: responseOrder } = useGetOrderDetail(order_id, { enabled: !!order_id });
  const order = useMemo(() => responseOrder?.data, [responseOrder?.data]);

  const [reportForm] = Form.useForm<
    ParamsUpdateSubmitReport & {
      submitter_id: string;
      approver_id: string;
      contentAndConclusion: string;
    }
  >();
  const is_structure_report = Form.useWatch('is_structure_report', reportForm);
  const { data: layoutResponse } = useGetLayouts({ offset: 0, limit: 100, active: true });
  const layoutsQuery = useMemo(() => layoutResponse?.data || [], [layoutResponse?.data]);
  const layouts = useMemo(
    () =>
      layoutsQuery.filter(
        (it) =>
          (it?.institution_id === study?.institution_id ||
            it?.institution_id === assign?.order?.institution_id ||
            it?.institution_id === order?.institution_id) &&
          (it?.status === LAYOUT_TYPE.PUBLIC || it?.user_id === userInfo?.id)
      ),
    [
      assign?.order?.institution_id,
      layoutsQuery,
      order?.institution_id,
      study?.institution_id,
      userInfo?.id,
    ]
  );
  const {
    data: responseMatchedOrder,
    isInitialLoading: isLoadingResponseMatchedOrder,
    isRefetching: isRefetchingResponseMatchedOrder,
    // isError: isErrorOrder, //? Handle error later
  } = useGetOrders(
    {
      offset: 0,
      limit: 100,
      study_id: study?.study_id,
      sort_field: 'patient_arrived_time',
      sort_order: SORT_ORDER.DESC,
    },
    {
      enabled: !!study?.study_id,
      onError(err) {
        message.error(t(err?.response?.data?.error_message));
      },
    }
  );

  const trimEmptyDiv = useCallback(
    (content: string) => {
      const substr = EMPTY_LINE;
      let str = content.trim();
      if (str.endsWith(substr)) {
        str = str.substring(0, str.length - substr.length);
      }
      if (str.startsWith(substr)) {
        str = str.substring(substr.length);
      }
      return str;
    },
    [EMPTY_LINE]
  );

  const splitContentConclusion = useCallback(
    (contentAndConclusion: string) => {
      if (!contentAndConclusion) return {};
      const splitData = contentAndConclusion.split(LOCK_EMPTY_LINE);
      const content = decodeHTMLEntities(trimEmptyDiv(splitData[0])).replace(CONTENT_TITLE, '');
      const conclusion = splitData?.[1]
        ? decodeHTMLEntities(trimEmptyDiv(splitData[1])).replace(CONCLUSION_TITLE, '')
        : '';
      return { content, conclusion };
    },
    [CONCLUSION_TITLE, CONTENT_TITLE, LOCK_EMPTY_LINE, trimEmptyDiv]
  );

  const mergeContentConclusion = useCallback(
    (content: string, conclusion: string) => {
      return `${CONTENT_TITLE}${content ?? ''}${EMPTY_LINE}${LOCK_EMPTY_LINE}${CONCLUSION_TITLE}${
        conclusion ?? ''
      }${EMPTY_LINE}`;
    },
    [CONCLUSION_TITLE, CONTENT_TITLE, EMPTY_LINE, LOCK_EMPTY_LINE]
  );

  const matchedOrders = useMemo(() => {
    if (order) {
      return [order];
    } else if (assign?.order) {
      return [assign?.order];
    } else if (study) {
      return responseMatchedOrder?.data;
    }
    return [];
  }, [
    assign?.order,
    order,
    responseMatchedOrder?.data,
    study,
  ]) as ReportFormStates['matchedOrders'];

  const [selectedOrder, setSelectedOrder] = useState<ReportFormStates['selectedOrder']>(null);
  const {
    data: reportResponse,
    isInitialLoading: isLoadingReport,
    isRefetching: isRefetchingReport,
  } = useGetLatestReportByOrder(selectedOrder?.id, {
    enabled: !!selectedOrder?.id,
    onError(err) {
      message.error(err?.response?.data?.error_message);
    },
  });
  const report = useMemo(() => reportResponse?.data, [reportResponse?.data]);
  const { data: reportVersionsResponse, refetch: refetchReportVersions } = useGetReportVersions(
    report?.id,
    { enabled: !!report?.id }
  );
  const reportVersions = useMemo(
    () => reportVersionsResponse?.data,
    [reportVersionsResponse?.data]
  );

  const [selectedReportVersion, setSelectedReportVersion] = useState(report?.report_version?.id);
  const isLatestReport = useMemo(
    () => report?.report_version?.id === selectedReportVersion,
    [report?.report_version?.id, selectedReportVersion]
  );
  const { data: selectedReportResponse } = useGetReportByVersionId(selectedReportVersion, {
    enabled: !!selectedReportVersion,
  });
  // const [esignProcessingID, setEsignProcessingID] = useState('');
  const [structureSections, setStructureSections] = useImmer<Section[]>(null);

  const selectedReport = useMemo(() => {
    const result = getArrayVal(selectedReportResponse?.data);
    // setEsignProcessingID(result?.esign_order?.id);
    setStructureSections(() => result?.structure?.data || []);
    return result;
  }, [selectedReportResponse?.data, setStructureSections]);

  const {
    data: esignResponse,
    refetch: refetchGetEsign,
    isFetching: isEsignFetching,
    isLoading: isEsignLoading,
    isRefetching: isEsignRefetching,
  } = useGetFullEsignHistoryByOrder(
    { limit: 1, offset: 0, order_id: selectedOrder?.id },
    {
      enabled: !!selectedOrder?.id,
      refetchInterval: (response) =>
        getArrayVal(response?.data)?.status === ESIGN_STATUS['ESIGN.Waiting'] ? 1000 : 0,
    }
  );

  const { status: esignStatus, processing_id: esignProcessingID } = useMemo(
    () =>
      getArrayVal(esignResponse?.data) || {
        status: '' as unknown as ESIGN_STATUS,
        processing_id: '',
      },
    [esignResponse?.data]
  );

  const isSignedStatus = useMemo(
    () =>
      [
        ESIGN_STATUS['Approver Signed'],
        ESIGN_STATUS['Submitter Signed'],
        ESIGN_STATUS['Approver Failed'],
        ESIGN_STATUS['Revoke Failed'],
      ].includes(esignStatus),
    [esignStatus]
  );

  const { data: PDFReportResponse } = useGetEsignHistoryDetail(esignProcessingID, {
    enabled: isSignedStatus && !!esignProcessingID,
  });

  const signed_report = useMemo(
    () => PDFReportResponse?.data?.signed_report,
    [PDFReportResponse?.data?.signed_report]
  );

  const isSignedReport = isSignedStatus && signed_report;

  const { data: PDFReport, isInitialLoading: isLoadingPDFReport } = useGetPrintPDF(signed_report, {
    enabled: !!signed_report,
  });

  useEffect(() => {
    setSelectedReportVersion(report?.report_version?.id);
  }, [report?.report_version?.id, reportResponse]);

  const structuredReportRef = useRef<StructuredReportHandle>(null);

  const displayedLayouts =
    selectedReport?.status === REPORT_STATUS.READING ? layouts : layoutsQuery;

  const [newReport, setNewReport] = useState<ResponseCreateReport>(null);
  const { mutate: saveReport, isLoading: isSavingReport } = useUpdateSubmitReport();
  const { mutate: approveReport, isLoading: isApprovingReport } = useUpdateApproveReport();
  const { mutate: editReport, isLoading: isEditingReport } = useUpdateEditReport();
  const { mutate: cancelReport, isLoading: isCancelingReport } = useUpdateCancelReport();

  const webcamRef = useRef<CaptureHandle>(null);

  const responseKeyImages = useGetKeyImages(
    study?.study_instance_uid || assign?.order?.study?.study_instance_uid,
    {
      enabled: !!(
        (study?.study_instance_uid || assign?.order?.study?.study_instance_uid) &&
        keycloak.hasOneOfPerm([PERMISSIONS.report.keyimage])
      ),
    }
  );

  const [structureReportSections, setStructureReportSections] = useState([]);
  const getStructureReport = useCallback(() => {
    if (!selectedReport?.is_structure_report) return;
    const { wait, cleanup } = waitForReady(
      () => {
        if (!selectedReport?.is_structure_report) return true;
        const response = structuredReportRef?.current?.getSections();
        return response?.length && addToArray(response, []).every(Boolean);
      },
      30_000,
      300
    );

    wait.then(() => {
      const result = structuredReportRef?.current?.getSections();
      setStructureReportSections(result);
    });

    return cleanup;
  }, [selectedReport?.is_structure_report]);

  useEffect(() => {
    if (!selectedReport?.is_structure_report) return;
    const cleanup = getStructureReport();

    return () => {
      cleanup?.();
    };
  }, [getStructureReport, selectedOrder, selectedReport?.is_structure_report]);

  const keyImages = useMemo(() => responseKeyImages?.data || [], [responseKeyImages?.data]);
  const {
    srcDoc,
    handlePrint,
    isLoading: isPrinting,
  } = useGenerateReport({
    report: Object.assign({}, report, selectedReport) satisfies MergedReport,
    order: selectedOrder,
    layout: layoutsQuery.find((it) => it?.id === reportForm.getFieldValue('layout_id')),
    study: study || assign?.order?.study,
    structureReportSections: structureReportSections,
  });

  useEffect(() => {
    //? Make sure api get matchedOrders done before call missing order
    if (matchedOrders?.length === 0) {
      onMissingOrder?.();
    }
    if (!selectedOrder) {
      const findOrder = order && matchedOrders && matchedOrders.find((it) => it.id === order.id);

      setSelectedOrder(findOrder || matchedOrders?.[0]);

      return;
    }
    const findOrder = matchedOrders?.find((it) => it.id === selectedOrder.id);

    setSelectedOrder(findOrder || matchedOrders?.[0]);
  }, [matchedOrders, order, selectedOrder]);

  useEffect(() => {
    reportForm.resetFields();
    if (newReport) {
      reportForm.setFieldsValue({
        submitter_id: null,
        approver_id: null,
        technician_id: validateEmptyString(newReport?.technician_id),
        contentAndConclusion: mergeContentConclusion('', ''),
        assign_to: userInfo?.id,
        is_structure_report: newReport?.is_structure_report,
      });
      if (newReport.is_structure_report) {
        setStructureSections(newReport?.structure?.data || []);
      }
    } else {
      reportForm.setFieldsValue({
        ...selectedReport,
        submitter_id: validateEmptyString(selectedReport?.submitter_id),
        approver_id:
          selectedReport?.status === REPORT_STATUS.READING
            ? null
            : validateEmptyString(selectedReport?.approver_id),
        technician_id: validateEmptyString(selectedReport?.technician_id),
        contentAndConclusion: mergeContentConclusion(
          selectedReport?.content,
          selectedReport?.conclusion
        ),
      });
      setStructureSections(selectedReport?.structure?.data || []);
    }
  }, [selectedReport, newReport, userInfo?.id]);

  const handleEditReport = () => {
    editReport(
      { id: report?.id, data: { order_assign_id: assign?.id } },
      {
        onSuccess: (res) => {
          console.log('EDIT REPORT', res);
        },
        onError: (error: UpdateEditReportErrorResponse) => {
          message.error({
            content: t(MESSAGE.ERROR[error?.response?.data?.error_code] || MESSAGE.ERROR.EMSG_0001),
          });
        },
      }
    );
  };

  const handleCancelReport = () => {
    cancelReport(
      { id: report.id, data: { order_assign_id: assign?.id, version_id: selectedReportVersion } },
      {
        onSuccess: (res) => {
          setNewReport(null);
          console.log('CANCEL EDIT REPORT', res);
        },
        onError: (error: UpdateEditReportErrorResponse) => {
          message.error({
            content: t(MESSAGE.ERROR[error?.response?.data?.error_code] || MESSAGE.ERROR.EMSG_0001),
          });
        },
      }
    );
  };

  const handleSave = useCallback(() => {
    function onSave() {
      reportForm
        .validateFields()
        .then((values) => {
          const { content, conclusion } = splitContentConclusion(values.contentAndConclusion);
          saveReport(
            {
              id: report?.id,
              data: {
                ...values,
                content,
                conclusion,
                structure: {
                  // data: structureSections,
                  data: structuredReportRef?.current?.getJsonSections(),
                },
                order_assign_id: assign?.id,
              },
            },
            {
              onSuccess: (res) => {
                setNewReport(null);
                console.log('SAVE REPORT', res);
              },
              onError: (error) => {
                message.error({
                  content: t(
                    MESSAGE.ERROR[error?.response?.data?.error_code] || MESSAGE.ERROR.EMSG_0001
                  ),
                });
              },
            }
          );
        })
        .catch((err) => {
          console.error('Save error', err);
        });
    }

    if (recordVideo.open) {
      return warningCloseCamera(
        async () => {
          webcamRef.current.stopRecord(true);
          await webcamRef?.current?.handleUploadFiles();
          onSave();
        },
        {
          title: t('You are recording a video. Do you want to stop recording and upload the data?'),
          okText: t('Save'),
          cancelText: t('Cancel'),
        }
      );
    }

    onSave();
  }, [
    recordVideo.open,
    reportForm,
    splitContentConclusion,
    saveReport,
    report?.id,
    assign?.id,
    message,
    t,
    warningCloseCamera,
  ]);

  const handleApprove = () => {
    reportForm
      .validateFields()
      .then((values) => {
        const { content, conclusion } = splitContentConclusion(values.contentAndConclusion);
        approveReport(
          {
            id: report.id,
            data: {
              is_structure_report: values.is_structure_report,
              assign_to: assign_to,
              indicator: reportForm.getFieldValue('indicator'),
              content,
              conclusion,
              structure: {
                // data: structureSections,
                data: structuredReportRef?.current?.getJsonSections(),
              },
              technician_id: values.technician_id,
              order_assign_id: assign?.id,
              layout_id: values.layout_id,
            },
          },
          {
            onSuccess: (res) => {
              setNewReport(null);
              console.log('APPROVE REPORT', res);
            },
            onError: (error) => {
              message.error({
                content: t(
                  MESSAGE.ERROR[error?.response?.data?.error_code] || MESSAGE.ERROR.EMSG_0001
                ),
              });
            },
          }
        );
      })
      .catch((err) => {
        console.error('Approve error', err);
      });
  };

  const canSeeReport = useCallback(
    (selected: typeof selectedReport) => {
      //? Report is editing by someone
      if (selected?.status === REPORT_STATUS.READING && report?.session !== userInfo?.id)
        return false;
      //? open from study can view all
      //? admin can view all
      if (study || keycloak.hasOneOfPerm([PERMISSIONS.assigned.admin_assignedlist])) return true;
      //? Report is in my processing
      if (report?.session === userInfo?.id) return true;
      //? Report submit by me
      return selected?.submitter_id === userInfo?.id;
    },
    [report?.session, study, userInfo?.id]
  );

  const showEditReport = useMemo(
    () =>
      //? Can edit report when: have selected report, that report is not reading and all report is [approved && have permission report.create, submitted]
      canSeeReport(selectedReport) &&
      !isEmpty(selectedReport) &&
      selectedReport?.status !== REPORT_STATUS.READING,
    // reports.every(
    //   (it) =>
    //     (it.status === REPORT_STATUS.APPROVED &&
    //       keycloak.hasOneOfPerm([PERMISSIONS.report.create])) ||
    //     it.status === REPORT_STATUS.SUBMITTED
    // ),
    [canSeeReport, selectedReport]
  );

  const showCancelReport = useMemo(
    () =>
      //? Show cancel button when: have selected report, that report is reading and session is me
      !isEmpty(selectedReport) &&
      selectedReport?.status === REPORT_STATUS.READING &&
      report?.session === userInfo.id,
    [report?.session, selectedReport, userInfo.id]
  );

  const breakpoint = iframeState?.open ? 1800 : 1300;
  // const windowSize = useWindowSize();
  const title = useMemo(() => {
    const arr = [
      selectedOrder?.visited_patient?.name || study?.patient_name,
      selectedOrder?.visited_patient?.patient_id || study?.patient_id,
      t(GENDER[selectedOrder?.visited_patient?.sex ?? study?.patient_sex]),
      selectedOrder?.visited_patient?.birth_date || study?.patient_birth_date
        ? dayjs(selectedOrder?.visited_patient?.birth_date || study?.patient_birth_date).format(
            getDateFormatted()
          )
        : study?.patient_age || '',
    ];

    return arr.filter(Boolean).join(' | ');
  }, [
    selectedOrder?.visited_patient?.birth_date,
    selectedOrder?.visited_patient?.name,
    selectedOrder?.visited_patient?.patient_id,
    selectedOrder?.visited_patient?.sex,
    study?.patient_age,
    study?.patient_birth_date,
    study?.patient_id,
    study?.patient_name,
    study?.patient_sex,
    t,
  ]);

  const paramsUploadRecorder = useMemo(() => {
    let headerParams = attachmentParams;

    if (study) {
      headerParams = {
        accession_number: getArrayVal(study.order_group),
        device: study.device_id,
        institution_id: study.institution_id,
        modality: getArrayVal(study.modality) || MODALITIES[13],
        patient_id: study.patient_id,
        ...conditionObj('patient_name', study.patient_name),
        study_datetime: study.study_date,
        ...conditionObj('study_description', getArrayVal(study.study_desc)),
        study_instance_uid: study.study_instance_uid,
      };
    } else if (assign) {
      headerParams = {
        accession_number: assign?.order?.order_group,
        device: assign?.order?.device_id,
        institution_id: assign?.order?.institution_id,
        modality: assign?.order?.procedure?.modality || MODALITIES[13],
        patient_id: assign?.order?.visited_patient?.patient_id,
        ...conditionObj('patient_name', assign?.order?.visited_patient?.name),
        study_datetime: assign?.order?.study?.study_date || assign?.order?.scheduled_date,
        ...conditionObj('study_instance_uid', assign?.order?.study?.study_instance_uid),
      };
    }

    return headerParams;
  }, [study, attachmentParams, assign]);

  const addOrderMenuItems: MenuItemType[] = [
    {
      key: '1',
      label: t('Create Order'),
      disabled: !keycloak.hasOneOfPerm([PERMISSIONS.order.create]),
      onClick: () => setOpenCreateOrder(true),
    },
    {
      key: '2',
      label: t('Match Order'),
      disabled: !keycloak.hasOneOfPerm([PERMISSIONS.studylist.match]),
      onClick: () => setOpenMatchOrder(true),
    },
  ];

  const onReloadingReport = useMemo(
    () =>
      isLoadingReport ||
      isRefetchingReport ||
      isLoadingResponseMatchedOrder ||
      isRefetchingResponseMatchedOrder,
    [
      isLoadingReport,
      isLoadingResponseMatchedOrder,
      isRefetchingReport,
      isRefetchingResponseMatchedOrder,
    ]
  );

  /*   const onChangeStructuredReportForm = useCallback(
    (sections: StructuredElement[]) => {
      setStructureSections(sections);
    },
    [setStructureSections]
  ); */

  const canEditReport = useMemo(() => {
    /**
     * 1. Disable if the report is currently being edited (`isEditingReport` is `true`)
     *
     * For Structure Reports:
     * 2. Enable if the user has both `PERMISSIONS.report.update` and `PERMISSIONS.report.structure`.
     * 3. Disable if the user has `PERMISSIONS.report.update` but no `PERMISSIONS.report.structure`.
     * 4. Disable if the user no `PERMISSIONS.report.update` but has `PERMISSIONS.report.structure`.
     * 5. Disable if the user lacks both `PERMISSIONS.report.update` and `PERMISSIONS.report.structure`.
     *
     * For normal Reports (`is_structure_report` is `false`):
     * 6. Enable if the user has `PERMISSIONS.report.update`.
     * 7. Disable if the user lacks `PERMISSIONS.report.update`.
     */
    if (isEditingReport) return false;

    const hasUpdatePermission = keycloak.hasOneOfPerm([PERMISSIONS.report.update]);
    const hasStructurePermission = keycloak.hasOneOfPerm([PERMISSIONS.report.structure]);
    const isStructureReport = selectedReport?.is_structure_report;

    if (isStructureReport) {
      if (hasUpdatePermission && hasStructurePermission) return true;
      if (hasUpdatePermission && !hasStructurePermission) return false;
      if (!hasUpdatePermission && hasStructurePermission) return false;
      if (!hasUpdatePermission && !hasStructurePermission) return false;
    } else if (hasUpdatePermission) return true;

    return false;
  }, [isEditingReport, selectedReport?.is_structure_report]);

  const canAssign = useMemo(() => {
    /**
     * Can Assign when:
     *
     * 1. Have permission `PERMISSIONS.report.assign`
     * 2. Order status !== ORDER_STATUS.READING, ORDER_STATUS.SUSPENDED, ORDER_STATUS.CANCEL]
     * 3. Match order = Matched (Have study_id)
     */
    const hasAssignPermission = keycloak.hasOneOfPerm([PERMISSIONS.report.assign]);
    if (!hasAssignPermission) return false;

    const DISALLOWED_STATUSES = [ORDER_STATUS.READING, ORDER_STATUS.SUSPENDED, ORDER_STATUS.CANCEL];
    const isStatusAllowed = !DISALLOWED_STATUSES.includes(selectedOrder?.status);
    if (!isStatusAllowed) return false;

    return Boolean(selectedOrder?.study_id);
  }, [selectedOrder?.status, selectedOrder?.study_id]);

  const canSignReport = useMemo(() => {
    if (
      isEsignFetching ||
      isEsignLoading ||
      isEsignRefetching ||
      [ESIGN_STATUS['ESIGN.Waiting'], ESIGN_STATUS['Revoking']].includes(esignStatus)
    ) {
      return {
        enable: false,
        title: t('This report is pending a digital signature. Please wait...'),
      };
    }

    if (!isLatestReport) {
      return {
        enable: false,
        title: t('Please select the final version of the report to use this feature.'),
      };
    }

    if (selectedOrder?.status !== ORDER_STATUS.APPROVED) {
      return {
        enable: false,
        title: t('This report needs to be approved first.'),
      };
    }

    if (selectedOrder?.status === ORDER_STATUS.APPROVED) {
      if (
        esignStatus === ESIGN_STATUS['Submitter Signed'] &&
        selectedReport?.approver_id !== userInfo?.id
      ) {
        return { enable: false, title: t('Only Approver can sign this report.') };
      }

      if (
        selectedReport?.approver_id !== userInfo?.id &&
        selectedReport?.approver_id === selectedReport?.submitter_id &&
        (typeof esignStatus !== 'number' ||
          [
            ESIGN_STATUS['Approver Failed'],
            ESIGN_STATUS['Submitter Failed'],
            ESIGN_STATUS['Revoked'],
            ESIGN_STATUS['Revoke Failed'],
            ESIGN_STATUS['Unsigned'],
          ].includes(esignStatus))
      ) {
        return { enable: false, title: t('Only Approver can sign this report.') };
      }

      if (
        selectedReport?.submitter_id !== userInfo?.id &&
        (typeof esignStatus !== 'number' ||
          [
            ESIGN_STATUS['Submitter Failed'],
            ESIGN_STATUS['Revoked'],
            ESIGN_STATUS['Revoke Failed'],
            ESIGN_STATUS['Unsigned'],
          ].includes(esignStatus))
      ) {
        return { enable: false, title: t('Only Submitter can sign this report.') };
      }
    }

    return { enable: true, title: '' };
  }, [
    esignStatus,
    isEsignFetching,
    isEsignLoading,
    isEsignRefetching,
    isLatestReport,
    selectedOrder?.status,
    selectedReport?.approver_id,
    selectedReport?.submitter_id,
    t,
    userInfo,
  ]);

  const titleEsign = useMemo(() => {
    switch (esignStatus) {
      case ESIGN_STATUS.Revoked:
        if (selectedReport?.submitter_id === selectedReport?.approver_id) return esignApprover;
        return esignSubmitter;
      case ESIGN_STATUS['ESIGN.Waiting']:
        return 'E-signing...';
      case ESIGN_STATUS['Submitter Signed']:
        return esignApprover;
      case ESIGN_STATUS['Approver Signed']:
        return;
      case ESIGN_STATUS['Approver Failed']:
        return esignApprover;
      default: {
        if (selectedReport?.approver_id === selectedReport?.submitter_id) {
          return esignApprover;
        }

        return esignSubmitter;
      }
    }
  }, [esignStatus, selectedReport?.approver_id, selectedReport?.submitter_id]);

  const titleEdit = useMemo(() => {
    if (
      [
        ESIGN_STATUS['Approver Signed'],
        ESIGN_STATUS['Submitter Signed'],
        ESIGN_STATUS['Approver Failed'],
        ESIGN_STATUS['Revoke Failed'],
      ].includes(esignStatus)
    ) {
      return 'This report has been digitally signed. Please revoke the signature to edit the report';
    }
  }, [esignStatus]);

  const canPrint = useMemo(() => {
    if (!keycloak.hasOneOfPerm([PERMISSIONS.report.print])) {
      return {
        enable: false,
        title: t('User lacks the permission report.print to print reports.'),
      };
    }

    if (selectedReport?.status !== REPORT_STATUS.APPROVED) {
      return { enable: false, title: t('The report is not approved.') };
    }

    if (!isLatestReport) {
      return { enable: false, title: t('The report is not the latest version.') };
    }

    if ([ESIGN_STATUS['Approver Signed']].includes(esignStatus) && !PDFReport) {
      return { enable: false, title: t('PDF report is not available.') };
    }

    return { enable: true, title: '' };
  }, [PDFReport, esignStatus, isLatestReport, selectedReport?.status, t]);

  const assign_to = Form.useWatch('assign_to', reportForm);

  useEffect(() => {
    // Set default value to report when is current session login and report status is reading
    if (selectedReport?.status !== REPORT_STATUS.READING || report?.session !== userInfo?.id)
      return;

    if (!selectedReport?.assign_to) reportForm.setFieldValue('assign_to', userInfo?.id);
  }, [
    report?.session,
    reportForm,
    selectedReport?.assign_to,
    selectedReport?.indicator,
    selectedReport?.status,
    userInfo?.id,
  ]);

  return (
    <div className={'flex w-full flex-col space-y-3 overflow-x-visible'} style={{ height: 'auto' }}>
      <ConfigProvider theme={{ token: { screenXXLMin: breakpoint, screenXXL: breakpoint } }}>
        <Form
          requiredMark
          className="form-filter-condition report-form"
          form={reportForm}
          labelAlign="left"
          layout="vertical"
          name="form"
        >
          <Row gutter={[16, 24]} style={{ overflow: 'hidden auto' }}>
            <KeyImageDisplay
              hasPermission={keycloak.hasOneOfPerm([PERMISSIONS.report.keyimage])}
              keyImages={keyImages}
              openRecordVideo={openRecordVideo}
            />
            <Col
              className="w-full"
              sm={24}
              // xl={keyImages.length ? (openRecordVideo ? 8 : 14) : 12}
              xxl={
                openRecordVideo
                  ? 16
                  : keyImages.length
                  ? isMobile()
                    ? 13
                    : 19
                  : isMobile()
                  ? 14
                  : 24
              }
            >
              <div className="flex flex-col">
                <div className="flex items-end justify-between">
                  <ReportHeader
                    diagnostic={selectedOrder?.diagnostic}
                    orderDoctorName={selectedOrder?.order_doctor_name}
                    title={title}
                  />
                  <Button
                    className="mt-1 mb-3 flex items-center"
                    size="small"
                    title={t('Clinical Information')}
                    onClick={openTestResult}
                  >
                    <InfoOutlined />
                  </Button>
                </div>

                <div className="flex justify-between">
                  <div className="my-2 flex flex-wrap space-x-2 space-x-reverse">
                    {/* {
                      //? Can create report when: have selected oder and all report is approved
                      selectedOrder &&
                        keycloak.hasOneOfPerm([PERMISSIONS.report.create]) &&
                        report?.status === REPORT_STATUS.APPROVED && (
                          <DropdownButton
                            className="mt-1 mr-2 flex items-center"
                            disabled={onReloadingReport}
                            menu={{ items: createReportMenuItems }}
                            placement="bottomLeft"
                          >
                            <Button className="mt-1 flex items-center" size="small" type="primary">
                              <PlusOutlined />
                              {t('Add New')}
                            </Button>
                          </DropdownButton>
                        )
                    } */}
                    {showEditReport ? (
                      <Button
                        className="mt-1 mr-2 flex items-center"
                        size="small"
                        title={t(titleEdit)}
                        disabled={
                          !canEditReport ||
                          onReloadingReport ||
                          !isLatestReport ||
                          [
                            ESIGN_STATUS['ESIGN.Waiting'],
                            ESIGN_STATUS['Submitter Signed'],
                            ESIGN_STATUS['Approver Signed'],
                            // ESIGN_STATUS['Approver Failed'], // truong hop nguoi submit == approver thi cho phep sua
                            ESIGN_STATUS.Revoking,
                          ].includes(esignStatus)
                          || (esignStatus === ESIGN_STATUS['Approver Failed'] && selectedReport?.submitter_id !== selectedReport?.approver_id)
                        }
                        onClick={handleEditReport}
                      >
                        <EditOutlined />
                        {t('Edit')}
                      </Button>
                    ) : null}
                    {showCancelReport && !isSignedReport ? (
                      <Button
                        danger
                        className="mt-1 mr-2 flex items-center"
                        size="small"
                        disabled={
                          isCancelingReport ||
                          onReloadingReport ||
                          !keycloak.hasOneOfPerm([PERMISSIONS.report.update])
                        }
                        onClick={handleCancelReport}
                      >
                        <CloseOutlined />
                        {t('Cancel')}
                      </Button>
                    ) : null}
                    <OpenWebcamButton
                      recordVideo={recordVideo}
                      setRecordVideo={setRecordVideo}
                      warningCloseCamera={warningCloseCamera}
                    />
                    {
                      //? Can print report when: have selected report, that report isn't reading
                      canSeeReport(selectedReport) && selectedReport ? (
                        <Button
                          className="ml-2 mt-1 flex min-w-16 items-center justify-center"
                          loading={isLoadingPDFReport || isPrinting}
                          size="small"
                          title={canPrint.title}
                          disabled={
                            // !(
                            //   keycloak.hasOneOfPerm([PERMISSIONS.report.print]) &&
                            //   selectedReport?.status === REPORT_STATUS.APPROVED
                            // ) ||
                            // !isLatestReport ||
                            // !PDFReport
                            !canPrint.enable
                          }
                          onClick={() => {
                            if (isSignedReport) return handlePrint(PDFReport);

                            const hasLayout = layoutsQuery.find(
                              (it) => it?.id === reportForm.getFieldValue('layout_id')
                            );

                            if (!hasLayout) return message.error(t('No layout selected yet'));
                            handlePrint(previewRef.current.editor?.iframeElement);
                          }}
                        >
                          <PrintOutlined />
                          {t('Print')}
                        </Button>
                      ) : null
                    }
                    {![ESIGN_STATUS['Approver Signed'], ESIGN_STATUS['Revoke Failed']].includes(
                      esignStatus
                    ) && keycloak.hasOneOfPerm([PERMISSIONS.report.sign_digital_create]) ? (
                      <SignDigital
                        buttonText={titleEsign}
                        content={srcDoc}
                        disabled={!canSignReport.enable}
                        message={message}
                        report_id={report?.id}
                        report_version_id={report?.report_version?.id}
                        title={canSignReport.title}
                        preview={
                          <PrintModal
                            ref={printRef}
                            order={selectedOrder}
                            structureReportSections={structureReportSections}
                            study={study || assign?.order?.study}
                            layout={layoutsQuery.find(
                              (it) => it?.id === reportForm.getFieldValue('layout_id')
                            )}
                            okText={
                              <>
                                <FileSignatureOutlined className="mr-[1ch]" />
                                {t('Sign Report')}
                              </>
                            }
                            report={
                              Object.assign({}, report, selectedReport) satisfies MergedReport
                            }
                          />
                        }
                        onSuccess={() => {
                          refetchGetEsign().then(({ data: esignResponse }) => {
                            const esignData = getArrayVal(esignResponse?.data);

                            if (esignData?.status !== ESIGN_STATUS['Approver Signed']) return;
                            // setEsignProcessingID(esignData?.processing_id);
                          });
                        }}
                      />
                    ) : null}
                    {!order_id && !assign && !!study ? ( // Can not create/match order if open from order, assign
                      <Dropdown
                        className="mt-1"
                        menu={{ items: addOrderMenuItems }}
                        placement="bottomLeft"
                        trigger={['click']}
                      >
                        <Button className="flex items-center" size="small">
                          {t('Add Order')}
                          <CaretDownOutlined className="ml-1" />
                        </Button>
                      </Dropdown>
                    ) : null}
                    {canAssign ? (
                      <Button
                        className="mt-1 flex items-center"
                        size="small"
                        onClick={() => setAssignState({ visible: true, order: selectedOrder })}
                      >
                        <AssignmentIndOutlined className="ml-1" />
                        {t('Assign')}
                      </Button>
                    ) : null}
                    <History
                      refetch={refetchReportVersions}
                      reportVersions={reportVersions}
                      selectedReportVersion={selectedReportVersion}
                      setSelectedReportVersion={setSelectedReportVersion}
                    />
                    {showCancelReport ? (
                      <Button
                        className="mt-1"
                        disabled={selectedReport?.status !== REPORT_STATUS.READING}
                        size="small"
                        onClick={() => setOpenSelectTemplate(true)}
                      >
                        {t('Choose Report Template')}
                      </Button>
                    ) : null}
                    {selectedReport && canSeeReport(selectedReport) ? (
                      <Tooltip placement="top" title={t('Print Layout')}>
                        <Form.Item
                          className="mb-0"
                          name="layout_id"
                          rules={[{ required: true, message: t(MESSAGE.INLINE.IEM_0001) }]}
                        >
                          <Select
                            className="min-w-[156px] flex-1"
                            getPopupContainer={(trigger) => trigger.parentElement}
                            optionLabelProp="label"
                            placeholder={t('Select Layout')}
                            size="small"
                            suffixIcon={<CaretDownOutlined />}
                            disabled={
                              selectedReport?.status !== REPORT_STATUS.READING ||
                              !canSeeReport(selectedReport)
                            }
                          >
                            {displayedLayouts.map((it) => (
                              <Select.Option key={it.id} label={it.name} value={it.id}>
                                {it.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Tooltip>
                    ) : null}
                    {canSeeReport(selectedReport) && !isCompare && reportVersions?.length ? (
                      <Button className="mt-1" size="small" onClick={openCompare}>
                        {t('Compare')}
                      </Button>
                    ) : null}
                    {canSeeReport(selectedReport) && isCompare ? (
                      <Button
                        className="mt-1 flex items-center"
                        size="small"
                        onClick={closeCompare}
                      >
                        <CloseOutlined />
                        {t('Close Compare')}
                      </Button>
                    ) : null}
                  </div>
                  {matchedOrders?.length ? (
                    <div className="flex items-center">
                      {t('Approved')}:{' '}
                      {matchedOrders.filter((it) => it.status === ORDER_STATUS.APPROVED).length}/
                      {matchedOrders.length}
                    </div>
                  ) : null}
                </div>
              </div>
              {
                /**
                 * ? Not match order yet: show empty message: No order
                 * ? Not select report yet: show empty message: No report
                 * ? Can see report: show report form
                 * ? Otherwise: Report is being edited by someone
                 */

                isLoadingReport ||
                isRefetchingReport ||
                isLoadingResponseMatchedOrder ||
                isRefetchingResponseMatchedOrder ? (
                  <div className="flex h-full items-center justify-center">
                    <Loading />
                  </div>
                ) : !matchedOrders?.length ? (
                  <div className="flex h-80 flex-col items-center justify-center">
                    <Empty
                      className="h-1/2 m-0 flex flex-col items-center justify-center text-lg"
                      image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <div>
                          <div>{t('No order available')}</div>
                          <div>{t('You need to match study with order or create new order')}</div>
                        </div>
                      }
                    />
                    {!!assign || !!order ? null : (
                      <div className="mt-4 flex space-x-2">
                        <Button
                          className="mr-2"
                          disabled={!keycloak.hasOneOfPerm([PERMISSIONS.studylist.match])}
                          size="middle"
                          onClick={() => setOpenMatchOrder(true)}
                        >
                          {t('Match Order')}
                        </Button>
                        <Button
                          className=""
                          disabled={!keycloak.hasOneOfPerm([PERMISSIONS.order.create])}
                          size="middle"
                          type="primary"
                          onClick={() => setOpenCreateOrder(true)}
                        >
                          {t('Create Order')}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="flex flex-wrap gap-2">
                      <ReportEditor
                        className="flex-1 basis-[400px]"
                        {...{
                          assign,
                          canSeeReport,
                          closeCompare,
                          esignStatus,
                          isCompare,
                          is_structure_report,
                          matchedOrders,
                          message,
                          onReloadingReport,
                          report,
                          selectedOrder,
                          selectedReport,
                          selectedReportVersion,
                          setEditOrder,
                          setNewReport,
                          setOpenSelectTemplate,
                          setSelectedOrder,
                          setSelectedReportVersion,
                          structureSections,
                          structuredReportRef,
                          webcamRef,
                        }}
                      />
                      {isCompare && (
                        <CompareEditor
                          className="flex flex-1 basis-[400px] flex-col"
                          selectedOrder={selectedCompareOrder}
                          setSelectedOrder={setSelectedCompareOrder}
                          onClose={closeCompare}
                          {...{
                            canSeeReport,
                            isCompare,
                            matchedOrders,
                            message,
                            order,
                            selectedReport,
                          }}
                        />
                      )}
                    </div>
                    {canSeeReport(selectedReport) && (
                      <footer className="flex flex-wrap justify-between align-bottom">
                        <section className="mr-2 inline-flex flex-wrap space-x-2 space-x-reverse">
                          <div className="mt-3 mr-2 w-32">
                            <div>{t('Submitted By')}</div>
                            <div className="space h-5">
                              {formatDateColumn(selectedReport?.submitted_at)}
                            </div>
                            <FormItem.Technician
                              disabled
                              name="submitter_id"
                              placeholder={t('Select')}
                              {...(selectedReport?.submitter?.id?.length && {
                                selected: {
                                  value: selectedReport?.submitter?.id,
                                  label:
                                    selectedReport?.submitter?.fullname ||
                                    selectedReport?.submitter?.username,
                                },
                              })}
                              noStyle
                              label={'Submitted By'}
                              required={false}
                            />
                          </div>
                          <div className="mt-3 w-32">
                            <div>{t('Approved By')}</div>
                            <div className="space h-5">
                              {selectedReport?.status === REPORT_STATUS.APPROVED &&
                                formatDateColumn(selectedReport?.approved_at)}
                            </div>
                            <FormItem.Technician
                              disabled
                              name="approver_id"
                              placeholder={t('Select')}
                              style={{ width: 100 }}
                              {...(selectedReport?.approver?.id?.length && {
                                selected: {
                                  value: selectedReport?.approver?.id,
                                  label:
                                    selectedReport?.approver?.fullname ||
                                    selectedReport?.approver?.username,
                                },
                              })}
                              noStyle
                              label={'Approved By'}
                              required={false}
                            />
                          </div>
                          <div className="mt-3 w-32">
                            <div>{t('Technician')}</div>
                            <div className="space min-[450px]:h-5" />
                            <FormItem.Technician
                              disabled={selectedReport?.status !== REPORT_STATUS.READING}
                              institutions={[selectedOrder?.institution?.code]}
                              placeholder={t('Select')}
                              {...(selectedReport?.technician?.id?.length && {
                                selected: {
                                  value: selectedReport?.technician?.id,
                                  label:
                                    selectedReport?.technician?.fullname ||
                                    selectedReport?.technician?.username,
                                },
                              })}
                              noStyle
                              label={'Technician'}
                              required={false}
                            />
                          </div>
                          <div className="mt-3 w-32">
                            <div>{t('Report Indicator')}</div>
                            <div className="space min-[450px]:h-5" />
                            <FormItem.Select
                              allowClear
                              noStyle
                              className="w-full"
                              disabled={selectedReport?.status !== REPORT_STATUS.READING}
                              dropdownMatchSelectWidth={false}
                              label={'Report Indicator'}
                              name="indicator"
                              placeholder={t('Select')}
                              required={false}
                              size="small"
                              wrapperClassName="w-full"
                              options={enumKeys(Omit(INDICATOR, ['None'])).map((x) => ({
                                label: t(x),
                                value: INDICATOR[x],
                              }))}
                            />
                          </div>
                          <div className="mt-3 w-32">
                            <div>{t('Assign To')}</div>
                            <div className="space min-[450px]:h-5" />
                            <FormItem.Technician
                              allowClear={false}
                              disabled={selectedReport?.status !== REPORT_STATUS.READING}
                              institutions={[selectedOrder?.institution?.code]}
                              placeholder={t('Select')}
                              {...(userInfo?.id && {
                                selected: {
                                  value: userInfo?.id,
                                  label: generateUserLabel(userInfo),
                                },
                              })}
                              noStyle
                              label={'Assign To'}
                              name="assign_to"
                              required={false}
                            />
                          </div>
                        </section>
                        <div className="footer mt-3 flex items-end justify-end">
                          {
                            //? Can save report when: have selected report, that report is reading and session is me
                            canSeeReport(selectedReport) &&
                            selectedReport?.status === REPORT_STATUS.READING &&
                            report?.session === userInfo?.id &&
                            !isSignedReport ? (
                              <Button
                                className="mr-2"
                                disabled={isUploading || isSavingReport}
                                type="primary"
                                onClick={handleSave}
                              >
                                {t('Save')}
                              </Button>
                            ) : null
                          }
                          {
                            //? Can approve report when: have selected report, that report is reding, submitted
                            canSeeReport(selectedReport) &&
                            [REPORT_STATUS.SUBMITTED, REPORT_STATUS.READING].includes(
                              selectedReport?.status
                            ) &&
                            assign_to === userInfo?.id &&
                            !isSignedReport ? (
                              <Button
                                className="mr-2"
                                type="primary"
                                disabled={
                                  isApprovingReport ||
                                  !keycloak.hasOneOfPerm([PERMISSIONS.report.approved]) ||
                                  !isLatestReport
                                }
                                onClick={handleApprove}
                              >
                                {t('Approve')}
                              </Button>
                            ) : null
                          }
                          {
                            //? Can print report when: have selected report, that report isn't reading
                            canSeeReport(selectedReport) && selectedReport ? (
                              <Button
                                // type="primary"
                                className=""
                                disabled={
                                  !keycloak.hasOneOfPerm([PERMISSIONS.report.print]) ||
                                  selectedReport?.status === REPORT_STATUS.READING ||
                                  !isLatestReport
                                }
                                onClick={() => {
                                  if (
                                    layoutsQuery.find(
                                      (it) => it?.id === reportForm.getFieldValue('layout_id')
                                    )
                                  ) {
                                    setOpenPrintDialog(true);
                                  } else {
                                    message.error(t('No layout selected yet'));
                                  }
                                }}
                              >
                                {t('Preview')}
                              </Button>
                            ) : null
                          }
                        </div>
                      </footer>
                    )}
                  </div>
                )
              }
            </Col>
            {openRecordVideo ? (
              <Col
                className="w-full min-[1200px]:h-full min-[1200px]:overflow-auto"
                lg={24}
                xxl={openRecordVideo ? 8 : 0}
              >
                <WebcamRecorder
                  ref={webcamRef}
                  assign={assign}
                  modal={modal}
                  open={openRecordVideo}
                  params={paramsUploadRecorder}
                  recordVideo={recordVideo}
                  setRecording={setRecordVideo}
                  study={study}
                  warningCloseCamera={warningCloseCamera}
                  onUploading={setIsUploading}
                />
              </Col>
            ) : null}
          </Row>
        </Form>

        {isOpenTestResult && (
          <TestResult
            className="flex flex-1 basis-[400px] flex-col"
            order={selectedOrder}
            visible={isOpenTestResult}
            onClose={closeTestResult}
          />
        )}
        <PrintModal
          ref={previewRef}
          open={openPrintDialog}
          handleClose={() => setOpenPrintDialog(false)}
          layout={layoutsQuery.find((it) => it?.id === reportForm.getFieldValue('layout_id'))}
          order={selectedOrder}
          pdfURL={PDFReport}
          report={Object.assign({}, report, selectedReport) satisfies MergedReport}
          structureReportSections={structureReportSections}
          study={study || assign?.order?.study}
        />
        <SelectTemplate
          is_structure_report={is_structure_report}
          modal={modal}
          order={selectedOrder}
          visible={openSelectTemplate}
          onCancel={() => setOpenSelectTemplate(false)}
          onSelect={(template) => {
            if (
              template?.layout_id &&
              displayedLayouts.find((it) => it?.id === template?.layout_id)
            ) {
              reportForm.setFieldValue('layout_id', template?.layout_id || '');
            }
            if (isStructureReport(template)) {
              if (is_structure_report) {
                setStructureSections(template?.sections);
                reportForm.setFieldValue(
                  'layout_id',
                  layouts.find((it) => it?.id === template?.layout_id) ? template?.layout_id : null
                );
                reportForm.setFieldValue('structure', template?.sections);
                // queryClient.invalidateQueries([queryKey.element_detail], { exact: false });
              }
            } else {
              reportForm.setFieldValue(
                'contentAndConclusion',
                mergeContentConclusion(template?.content || '', template?.conclusion || '')
              );
            }
            setOpenSelectTemplate(false);
          }}
        />
        {openMatchOrder ? (
          <MatchOrder
            message={message}
            pendingOrderIds={pendingOrderIds}
            study={study}
            visible={openMatchOrder}
            onCancel={() => setOpenMatchOrder(false)}
          />
        ) : null}
        {openCreateOrder ? (
          <CreateOrderModal
            defaultInstitutionId={keycloak.userInfo?.default_institution_id}
            disablePatientSearch={study?.match_status === MATCH_STATUS.Matched}
            institutionId={study?.institution_id}
            message={message}
            visible={openCreateOrder}
            defaultValues={
              study?.match_status === MATCH_STATUS.Matched
                ? {
                    patient_name: study?.patient_name,
                    birth_date: study?.patient_birth_date,
                    sex: study?.patient_sex,
                    patient_id: study?.patient_id,
                    device_id: study?.device_id,
                    visit_id: study?.visit_id,
                    // order_group: get(study?.order_group, '0', ''),
                    refer_doctor_name: get(study?.refer_doctor_name, '0', ''),
                    order_doctor_name: get(study?.order_doctor_name, '0', ''),
                    diagnostic: get(study?.diagnostic, '0', ''),
                    patient_age: study?.patient_age,
                  }
                : {
                    patient_name: study?.patient_name,
                    birth_date: study?.patient_birth_date,
                    study_date: study?.study_date,
                    sex: study?.patient_sex,
                    patient_id: study?.patient_id,
                    patient_age: study?.patient_age,
                    device_id: study?.device_id,
                  }
            }
            onCancel={() => setOpenCreateOrder(false)}
            onFinish={(orderIds) => {
              setPendingOrderIds(orderIds);
              setOpenMatchOrder(true);
              setOpenCreateOrder(false);
            }}
          />
        ) : null}
        {editOrder?.open ? (
          <EditOrder
            isEditing={editOrder?.open}
            message={message}
            orderData={editOrder?.editingOrder}
            onFinish={() => {
              setEditOrder({
                editingOrder: null,
                open: false,
              });
            }}
          />
        ) : null}
        {assignState.order ? (
          <Assign
            message={message}
            modal={modal}
            order={assignState.order}
            visible={assignState.visible}
            onCancel={() => setAssignState({ visible: false, order: null })}
          />
        ) : null}
      </ConfigProvider>
    </div>
  );
};

export default memo(
  ReportForm,
  (prev, next) =>
    isEqual(prev.study_id, next.study_id) &&
    isEqual(prev.assign, next.assign) &&
    isEqual(prev.attachmentParams, next.attachmentParams) &&
    isEqual(prev.recordVideo, next.recordVideo) &&
    isEqual(prev.order_id, next.order_id)
);

export interface ReportFormStates {
  assign: Parameters<typeof ReportForm>[0]['assign'];
  attachmentParams: Parameters<typeof ReportForm>[0]['attachmentParams'];
  canSeeReport: (report: ReportFormStates['selectedReport']) => boolean;
  displayedLayouts: ReturnType<typeof useGetLayouts>['data']['data'];
  isLoadingReportList: boolean;
  isLoadingReportsQuery: boolean;
  isLoadingResponseMatchedOrder: boolean;
  isRefetchingReportList: boolean;
  isRefetchingReportsQuery: boolean;
  isRefetchingResponseMatchedOrder: boolean;
  is_structure_report: boolean;
  keyImages: ReturnType<typeof useGetKeyImages>['data'];
  layouts: ReturnType<typeof useGetLayouts>['data']['data'];
  matchedOrders: (
    | ReturnType<typeof useGetOrders>['data']['data'][0]
    | Parameters<typeof Report>[0]['assign']['order']
  )[];
  message: Parameters<typeof ReportForm>[0]['message'];
  modal: Parameters<typeof ReportForm>[0]['modal'];
  openRecordVideo: Parameters<typeof ReportForm>[0]['recordVideo']['open'];
  order: ReturnType<typeof useGetOrderDetail>['data']['data'];
  reportForm: FormInstance<ParamsUpdateSubmitReport>;
  recordVideo: any;
  reports: ReturnType<typeof useGetLatestReportByOrder>['data']['data'];
  selectedOrder:
    | ReturnType<typeof useGetOrders>['data']['data'][number]
    | Parameters<typeof Report>[0]['assign']['order'][][number];
  selectedReport: ReturnType<typeof useGetReportByVersionId>['data']['data'];
  sessionUser: ReturnType<typeof useGetAccounts>['data']['data'][0];
  setIsUploading: Dispatch<SetStateAction<boolean>>;
  setOpenCreateOrder: Dispatch<SetStateAction<boolean>>;
  setOpenMatchOrder: Dispatch<SetStateAction<boolean>>;
  setOpenSelectTemplate: Dispatch<SetStateAction<boolean>>;
  showCancelReport: boolean;
  showEditReport: boolean;
  setRecordVideo: Parameters<typeof ReportForm>[0]['setRecordVideo'];
  setSelectedOrder: Dispatch<SetStateAction<ReportFormStates['selectedOrder']>>;
  setSelectedReport: Dispatch<SetStateAction<ReportFormStates['selectedReport']>>;
  study: ReturnType<typeof useGetDetailStudy>['data']['data'][0];
  webcamRef: MutableRefObject<CaptureHandle>;
  setEditOrder: Dispatch<SetStateAction<{ editingOrder: any; open: boolean }>>;
  esignStatus: ESIGN_STATUS;
  setNewReport: Dispatch<SetStateAction<ResponseCreateReport>>;
  onReloadingReport: boolean;
  structuredReportRef: MutableRefObject<StructuredReportHandle>;
  structureSections: Section[];
  report: LatestReportByOrder;
  selectedReportVersion: string;
  setSelectedReportVersion: Dispatch<SetStateAction<string>>;
}

export type RecordVideo = {
  open: boolean;
  recording: boolean;
};

export type StateReportParams = {
  recordVideo: RecordVideo;
  setRecordVideo: Updater<RecordVideo>;
  warningCloseCamera: (
    callback: any,
    settings?: {
      title?: any;
      content?: any;
      okText?: any;
      cancelText?: any;
    }
  ) => void;
};

export type ReportFormProps = Omit<Parameters<typeof Report>[0], 'visible'> & StateReportParams;

const [esignSubmitter, esignApprover] = ['E-sign (Submitter)', 'E-sign (Approver)'];
